/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : SelectVehicleSystemDialog.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import SelectZone from "./SelectZone";
import CommonBreadcrumbs from "../../../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import SelectComponentList from "./SelectComponentList";
import { adminSettingsAPI, HandleApiActions, labCar } from "../../../../redux/actions";
import softwareIntersectionAPI from "../../../../redux/actions/softwareIntersectionActions";
import { useDispatch, useSelector } from "react-redux";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CustomCheckBoxWithoutFormControl from "../../../../components/customComponentsV2/common/checkBox/CustomCheckBoxWithoutFormControl";


import CustomToolTip from "../../../../components/infoButton/CustomToolTip";
import CustomDialogBoxV2 from "../../../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomSelectV2 from "../../../../components/customComponentsV2/common/select/CustomSelectV2";
import SelectDialogActionButtons from "./SelectDialogActionButtons";

function SelectVehicleSystemDialog({
  isZonalProject,
  zoneList = [],
  selectedZone,
  setSelectedZone,
  showZone,
  setShowZone,
  selectedVS,
  setSelectedVS,
  setSelectedZoneName,
  selectedZoneName,
  selectedVC,
  setSelectedVC,
  disabledVSComponents = [],
  selectedLegalMain,
  requireVehicleComponentSelection = true,
  isEditMode = false,
}) {
  const zone = "zone";
  const vs = "vs";
  const vc = "vc";

  const dispatch = useDispatch();

  const [vsModifiedList, setVSModifiedList] = useState([]);
  const [vcModifiedList, setVCModifiedList] = useState([]);
  const [currentViewingVS, setCurrentViewingVS] = useState(null);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(25);
  const [projectID, setProjectID] = useState(null);
  const [variantID, setVariantID] = useState(null);
  const [disabledVCComponents, setDisabledVCComponents] = useState([]);

  const [openSupplierDialog, setOpenSupplierDialog] = useState(false);
  const [pendingVehicleComponent, setPendingVehicleComponent] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [pendingTargetType, setPendingTargetType] = useState(""); // "vs" or "vc"
  const adminSettingsDetails = useSelector(
    (state) => state?.adminSettingsReducer,
  );
  const [ecuSuppliersList, setEcuSuppliersList] = useState([]);

  useEffect(() => {
    const fetchAdminSuppliers = async () => {
      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...adminSettingsAPI.get_supplier_details,
            params: { page_number: 1, page_size: 100 },
            show_toast: false,
            ignore_app_loading_spinner: true,
          }),
        ),
      );
      const list =
        resp?.data?.ecu_suppliers ??
        resp?.data ??
        resp?.response?.data ??
        [];
      if (Array.isArray(list) && list.length > 0) {
        setEcuSuppliersList(list);
      }
      if (!adminSettingsDetails?.adminSettings?.data?.ecu_suppliers?.length) {
        void dispatch(
          HandleApiActions({
            ...adminSettingsAPI.get_admin_settings,
            show_toast: false,
            ignore_app_loading_spinner: true,
          }),
        );
      }
    };
    void fetchAdminSuppliers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const normalizeSupplierOptions = (list) => {
    if (!list) return [];
    const arr = Array.isArray(list) ? list : [list];
    return arr
      .map((opt) => {
        if (!opt) return null;
        if (typeof opt === "string") {
          const trimmed = opt.trim();
          return trimmed ? { value: trimmed, label: trimmed } : null;
        }
        const value =
          opt.value ?? opt.name ?? opt.supplier_name ?? opt.id ?? opt.label;
        const label =
          opt.label ?? opt.name ?? opt.supplier_name ?? opt.value ?? opt.id;
        if (value == null || label == null) return null;
        const strVal = String(value).trim();
        const strLabel = String(label).trim();
        return strVal ? { value: strVal, label: strLabel || strVal } : null;
      })
      .filter(Boolean);
  };

  const getSuppliersList = (component) => {
    if (!component) return [];
    const rawComponentSuppliers =
      component.suppliers ||
      component.supplier_name ||
      component.supplier ||
      component.vehicle_component_details?.suppliers ||
      component.vehicle_component_details?.supplier_name ||
      component.vc_details?.suppliers;

    const componentOptions = normalizeSupplierOptions(rawComponentSuppliers);
    if (componentOptions.length > 0) {
      return componentOptions;
    }

    // Fallback: parent VS selected_supplier
    const parentOptions = [];
    if (currentViewingVS?.selected_supplier) {
      parentOptions.push({
        label: currentViewingVS.selected_supplier,
        value: currentViewingVS.selected_supplier,
      });
    }

    // Fallback: ECU suppliers from admin settings
    const storeSuppliers =
      adminSettingsDetails?.adminSettings?.data?.ecu_suppliers || [];
    const ecuOptions = normalizeSupplierOptions(
      ecuSuppliersList.length > 0 ? ecuSuppliersList : storeSuppliers,
    );

    const combined = [...parentOptions, ...ecuOptions];
    const seen = new Set();
    const uniqueOptions = [];
    for (const opt of combined) {
      if (opt?.value && !seen.has(opt.value)) {
        seen.add(opt.value);
        uniqueOptions.push(opt);
      }
    }
    return uniqueOptions;
  };

  const selectedProjectDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );
  const selectedSingleVariantDetails = useSelector(
    (state) => state.projectConfigReducer.selectedVariantDetails,
  );

  useEffect(() => {
    if (selectedProjectDetails?.project_id) {
      setProjectID(selectedProjectDetails?.project_id);
    }
    if (selectedSingleVariantDetails?.variant_id) {
      setVariantID(selectedSingleVariantDetails?.variant_id);
    }
  }, [selectedProjectDetails, selectedSingleVariantDetails]);

  const modifyVSData = (data) => {
    const modifiedList = data.map((item) => ({
      data: item,
      id: item.id,
      cardDataArray: [
        { key: "Name", value: item.name, keyStyle: { fontWeight: 600 } },
        { key: "Description", value: item.description },
      ],
    }));
    setVSModifiedList(modifiedList);
  };

  const modifyVCData = (data) => {
    const modifiedList = data.map((item) => ({
      data: item,
      id: item.id,
      cardDataArray: [
        { key: "Name", value: item.name, keyStyle: { fontWeight: 600 } },
        { key: "Component Number", value: item.component_number },
      ],
    }));
    setVCModifiedList(modifiedList);
  };

  const getChangedZone = (zone, zoneName) => {
    setSelectedZone(zone);
    setSelectedZoneName(zoneName);
    setVSModifiedList([]); // clear stale list so Select All state resets before new zone data loads
    setShowZone(vs);
  };

  const getDisabledVCListForLR = async ({ lrId, vsId }) => {
    if (!lrId || !vsId) {
      setDisabledVCComponents([]);
      return;
    }
    if (!projectID || !variantID) {
      setDisabledVCComponents([]);
      return;
    }
    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...softwareIntersectionAPI.get_vc_for_legal_requirements,
          params: {
            project_id: Number(projectID),
            variant_id: Number(variantID),
            lr_id: Number(lrId),
            vs_id: Number(vsId),
          },
          show_toast: false,
          ignore_app_loading_spinner: true,
        }),
      )
    );
    if (resp.error) {
      setDisabledVCComponents([]);
      return;
    }
    const vcIds = resp?.data?.[0]?.vc_ids || [];
    const normalizedIds = vcIds
      .map(Number)
      .filter((id) => Number.isFinite(id));
    setDisabledVCComponents(normalizedIds);
  };

  const unselectVSHelper = (prev, id) => (prev || []).filter((ele) => ele.id !== id);

  const removeVCHelper = (vs, vcId) => ({
    ...vs,
    vcData: (vs.vcData || []).filter((vc) => vc.id !== vcId),
  });

  const updateVCItemHelper = (vs, data) => {
    if (vs.id === data.pr_vs_id) {
      return removeVCHelper(vs, data.id);
    }
    return vs;
  };

  const isNonEmptyVC = (vs, prVsId) => {
    if (vs.id === prVsId) {
      return Boolean(vs.vcData && vs.vcData.length > 0);
    }
    return true;
  };

  const unselectVCHelper = (prev, data) =>
    (prev || [])
      .map((vs) => updateVCItemHelper(vs, data))
      .filter((vs) => isNonEmptyVC(vs, data.pr_vs_id));

  const handleVCCheckboxChange = (e, data, isDisabled) => {
    e.stopPropagation();
    if (isDisabled) return;
    if (e.target.checked) {
      setPendingVehicleComponent(data);
      setPendingTargetType("vc");
      setSelectedSupplier("");
      setOpenSupplierDialog(true);
    } else {
      setSelectedVS((prev) => unselectVCHelper(prev, data));
    }
  };

  const handleVCCheckboxVSUpdate = (vs, componentWithSupplier) => {
    if (vs.id !== componentWithSupplier.pr_vs_id) return vs;
    const safeVcData = Array.isArray(vs.vcData) ? vs.vcData : [];
    if (safeVcData.some((vc) => vc.id === componentWithSupplier.id)) return vs;
    return { ...vs, vcData: [...safeVcData, componentWithSupplier] };
  };

  const updateVSSupplier = (safePrev, componentWithSupplier) => {
    const existing = safePrev.some((item) => item?.id === componentWithSupplier.id);
    if (existing) {
      return safePrev.map((item) => {
        if (item.id === componentWithSupplier.id) {
          return { ...item, selected_supplier: selectedSupplier };
        }
        return item;
      });
    }
    return [...safePrev, { ...componentWithSupplier, vcData: [] }];
  };

  const updateVCSupplier = (safePrev, componentWithSupplier) => {
    const parentExists = safePrev.some((vs) => vs.id === componentWithSupplier.pr_vs_id);
    if (parentExists) {
      return safePrev.map((vs) => handleVCCheckboxVSUpdate(vs, componentWithSupplier));
    }
    return [
      ...safePrev,
      {
        id: componentWithSupplier.pr_vs_id,
        name: currentViewingVS?.name || "Vehicle System",
        description: currentViewingVS?.description || "",
        vcData: [componentWithSupplier],
      },
    ];
  };

  const getSafePrevArray = (prev) => {
    if (Array.isArray(prev)) return prev;
    if (prev) return [prev];
    return [];
  };

  const handleVSCheckboxChange = (e, data, isDisabled) => {
    if (isDisabled) return;
    if (e.target.checked) {
      setPendingVehicleComponent(data);
      setPendingTargetType("vs");
      setSelectedSupplier("");
      setOpenSupplierDialog(true);
    } else {
      setSelectedVS((prev) => unselectVSHelper(prev, data.id));
    }
  };

  const handleAddSupplier = () => {
    if (pendingVehicleComponent && selectedSupplier) {
      const componentWithSupplier = {
        ...pendingVehicleComponent,
        selected_supplier: selectedSupplier,
      };
      if (pendingTargetType === "vs") {
        setSelectedVS((prev) => updateVSSupplier(getSafePrevArray(prev), componentWithSupplier));
      } else if (pendingTargetType === "vc") {
        setSelectedVS((prev) => updateVCSupplier(getSafePrevArray(prev), componentWithSupplier));
      }
    }
    resetSupplierDialog();
  };

  const resetSupplierDialog = () => {
    setOpenSupplierDialog(false);
    setPendingVehicleComponent(null);
    setSelectedSupplier("");
    setPendingTargetType("");
  };

  return (
    <Box>
      {showZone === zone && (
        <Box>
          <Box sx={{ mb: 1 }}>
            <CommonBreadcrumbs
              color={"black"}
              items={[{ label: "Select Zone" }]}
            />
          </Box>
          <SelectZone
            zoneList={zoneList}
            getChangedZone={getChangedZone}
            selectedZone={selectedZone}
          />
        </Box>
      )}

      {showZone === vs && (
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <CommonBreadcrumbs
              color={"black"}
              items={[
                ...(isZonalProject
                  ? [
                    {
                      label: selectedZoneName,
                      onClick: () => setShowZone(zone),
                    },
                  ]
                  : []),
                { label: "Vehicle System" },
              ]}
            />
          </Box>

          <Box>
            <SelectComponentList
              key={`ee-${projectID}-${variantID}-${selectedZone}`}
              dataList={vsModifiedList}
              apiAction={labCar.get_vehicle_systems}
              searchKey={"name"}
              placeholder={"Search Vehicle System"}
              page={page}
              rowsPerPage={rowsPerPage}
              handlePageChange={(page) => setPage(page)}
              totalCount={count}
              isItemDisabled={(item) =>
                disabledVSComponents.includes(Number(item?.data?.id))
              }
              renderSelectIcon={(data) => {
                if (requireVehicleComponentSelection) {
                  return null;
                }
                const isDisabled = disabledVSComponents.includes(
                  Number(data.id),
                );
                return (
                  <Box>
                    <CustomCheckBoxWithoutFormControl
                      value={
                        (selectedVS || [])?.some(
                          (ele) => ele.id === data.id,
                        ) || false
                      }
                      disabled={isDisabled}
                      onChange={(e) => handleVSCheckboxChange(e, data, isDisabled)}
                    />
                  </Box>
                );
              }}
              renderCardActionIcons={(data) => {
                const isDisabled = disabledVSComponents.includes(
                  Number(data.id),
                );
                const hasVehicleComponents = data?.is_vc !== false;
                const disableVehicleComponentNavigation =
                  isDisabled || !hasVehicleComponents;
                return (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                      gap: 0.5,
                      pt: 2,
                      pr: 2,
                      ...(disableVehicleComponentNavigation && {
                        cursor: "pointer",
                        opacity: 0.5,
                      }),
                    }}
                    onClick={() => {
                      if (disableVehicleComponentNavigation) return;
                      setCurrentViewingVS(data);
                      setShowZone(vc);
                      if (selectedLegalMain?.lr_id) {
                        getDisabledVCListForLR({
                          lrId: selectedLegalMain.lr_id,
                          vsId: data.id,
                        });
                      } else {
                        setDisabledVCComponents([]);
                      }
                    }}
                  >
                    {!hasVehicleComponents && (
                      <CustomToolTip
                        type="component"
                        title={
                          "There is no vehicle Component"
                        }
                      >
                        <ErrorOutlineIcon
                          fontSize="small"
                          sx={{ color: "black" }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </CustomToolTip>
                    )}
                    <ChevronRightIcon
                      sx={{
                        color: disableVehicleComponentNavigation
                          ? "#9E9E9E"
                          : "inherit",
                      }}
                    />
                  </Box>
                );
              }}
              getDataOnSearch={(data, resp) => {
                if (resp.error) {
                  modifyVSData([]);
                } else {
                  modifyVSData(resp.data);
                  setCount(resp.total_count);
                }
              }}
              params={{
                project_id: Number(projectID),
                variant_id: Number(variantID),
                ...(isZonalProject && { zone_position: selectedZone }),
                status: "true",
                page_number: page,
                page_size: rowsPerPage,
              }}
              disabled={
                !projectID ||
                !variantID ||
                projectID === 0 ||
                variantID === 0
              }
            />
          </Box>
        </Box>
      )}

      {showZone === vc && (
        <Box>
          <Box sx={{ mb: 1 }}>
            <CommonBreadcrumbs
              color={"black"}
              items={[
                {
                  label: currentViewingVS?.name,
                  onClick: () => setShowZone(vs),
                },
                { label: "Vehicle Component" },
              ]}
            />
          </Box>
          <Box>
            <SelectComponentList
              key={`vc-${projectID}-${variantID}-${currentViewingVS?.id}`}
              dataList={vcModifiedList}
              apiAction={labCar.get_vehicle_components}
              searchKey={"name"}
              placeholder={"Search Vehicle Component"}
              page={page}
              rowsPerPage={rowsPerPage}
              handlePageChange={(page) => setPage(page)}
              totalCount={count}
              isItemDisabled={(item) =>
                disabledVCComponents.includes(Number(item?.data?.id))
              }
              renderSelectIcon={(data) => {
                const isParentSelected = (selectedVS || []).some((vs) => vs.id === data.pr_vs_id);
                const isDisabled =
                  disabledVCComponents.includes(Number(data.id)) ||
                  (!requireVehicleComponentSelection && !isParentSelected);
                const isChecked = (selectedVS || []).some(
                  (vs) => vs.id === data.pr_vs_id && (vs.vcData || []).some((vc) => vc.id === data.id)
                );
                return (
                  <Box sx={{ pl: 1.5, pt: 2 }}>
                    <CustomCheckBoxWithoutFormControl
                      value={isChecked}
                      disabled={isDisabled}
                      onChange={(e) => handleVCCheckboxChange(e, data, isDisabled)}
                    />
                  </Box>
                );
              }}
              renderCardActionIcons={() => (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    pt: 2,
                    pr: 2,
                  }}
                />
              )}
              getDataOnSearch={(data, resp) => {
                if (resp.error) {
                  modifyVCData([]);
                } else {
                  modifyVCData(resp.data);
                  setCount(resp.total_count);
                }
              }}
              params={{
                project_id: Number(projectID),
                variant_id: Number(variantID),
                vs_id: currentViewingVS?.id,
                status: "true",
                page_number: page,
                page_size: rowsPerPage,
              }}
              disabled={!projectID || !variantID || !currentViewingVS?.id}
            />
          </Box>
        </Box>
      )}
      <CustomDialogBoxV2
        title="Select Supplier"
        open={openSupplierDialog}
        onClose={resetSupplierDialog}
        dialogActions={
          <SelectDialogActionButtons
            enableSubmit={
              Boolean(selectedSupplier) ||
              getSuppliersList(pendingVehicleComponent).length === 0
            }
            onClickAdd={handleAddSupplier}
            onClickCancel={resetSupplierDialog}
          />
        }
      >
        <Box sx={{ p: 2, minWidth: "300px" }}>
          <CustomSelectV2
            label="Select Supplier"
            optionsList={getSuppliersList(pendingVehicleComponent)}
            selectedValue={selectedSupplier}
            handleOnChange={(e) => setSelectedSupplier(e.target.value)}
          />
        </Box>
      </CustomDialogBoxV2>
    </Box>
  );
}

SelectVehicleSystemDialog.propTypes = {
  isZonalProject: PropTypes.bool,
  zoneList: PropTypes.array,
  selectedZone: PropTypes.any,
  setSelectedZone: PropTypes.func,
  showZone: PropTypes.string,
  setShowZone: PropTypes.func,
  selectedVS: PropTypes.array,
  setSelectedVS: PropTypes.func,
  setSelectedZoneName: PropTypes.func,
  selectedZoneName: PropTypes.string,
  selectedVC: PropTypes.any,
  setSelectedVC: PropTypes.func,
  disabledVSComponents: PropTypes.array,
  selectedLegalMain: PropTypes.shape({
    lr_id: PropTypes.any,
  }),
  requireVehicleComponentSelection: PropTypes.bool,
  isEditMode: PropTypes.bool,
};

export default SelectVehicleSystemDialog;
